import { Opportunity } from "@entities";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import { CircularProgress, IconButton, Stack } from "@mui/material";
import type { PartnerOrganization } from "@entities";
import { opportunityRepo } from "@repositories";
import { linko } from "@shared/linko";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type FC } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { COLORS } from "../../../scss/Variables";
import {
  ADMIN_GROUP_DELETE_ARCHIVE,
  ADMIN_GROUP_DELETE_HINT,
  ADMIN_GROUP_INSTANCE_BREADCRUMB_PARENT,
  ADMIN_GROUP_RESTORE_ARCHIVE,
} from "@shared/helpers/opportunity-group-admin-copy";
import { EventDetails } from "../../components/event-details/EventDetails";
import { EventRegistrationAnalysis } from "../../components/event-registration-analysis/EventRegistrationAnalysis";
import { EventParticipants } from "../../components/event-participants/EventParticipants";
import { EventRequests } from "../../components/event-requests/EventRequests";
import { EventSideBar } from "../../components/event-sidebar/EventSidebar";
import { SendInDistribution } from "../../components/popups/send-in-distribution/SendInDistribution";
import { useAlert } from "../../contexts/AlertProvider";
import { useRequest } from "../../contexts/RequestProvider";
import useQuestion from "../../dialog-components/QuestionDialog";
import MissionList from "../missions/components/MissionList";
import { OrganizationRequestFullContainer } from "../organization-request/OrganizationRequestFullContainer";
import OpportunityReportTable from "../tables/opportunity-report-table";
import { invalidateOpportunitiesTableQueries } from "../../queries/invalidate-opportunities-table";
import { GroupOpportunitySlots } from "../../components/group-opportunity-slots/GroupOpportunitySlots";
import { PartnerOrganizationDetailsDialog } from "../tables/partner-organizations-table/PartnerOrganizationDetailsDialog";
import { loadPartnerOrganizationForDetails } from "../tables/partner-organizations-table/partner-organization-details.helper";
import { RelevantVolunteersSection } from "../tables/opportunities-table/RelevantVolunteersSection";
import { CommunicationRecordManager } from "../../components/CommunicationRecordManager/CommunicationRecordManager";
import {
  OpportunityHandlingControls,
} from "../../components/opportunity-handling/OpportunityHandlingControls";
import { OpportunityHandlingEntryNotice } from "../../components/opportunity-handling/OpportunityHandlingEntryNotice";
import { CallCenterVolunteerAssignmentMark } from "../../components/call-center/CallCenterVolunteerAssignmentMark";
import { useUser } from "../../contexts/UserProvider";

import "./EventCard.scss";

export const EventCard: FC<{
  embeddedEventId?: string;
  /** When set, the card stays on this section (call-center tour). */
  selectedListOverride?: string;
  tourAnchors?: boolean;
  /** Show the group-add tab during the tour, on the real participants tables. */
  tourShowGroups?: boolean;
}> = ({
  embeddedEventId,
  selectedListOverride,
  tourAnchors = false,
  tourShowGroups = false,
}) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedListFromUrl = searchParams.get("selectedList");
  const isRelevantVolunteersStandalone =
    searchParams.get("view") === "relevant-volunteers-only";
  const [selectedList, setSelectedList] = useState<string>(
    location.state?.selectedList ??
      selectedListFromUrl ??
      (isRelevantVolunteersStandalone ? "relevant-volunteers" : ""),
  );
  const { eventId: routeEventId } = useParams<"eventId">();
  const eventId = embeddedEventId ?? routeEventId;
  const navigate = useNavigate();
  const { setAlertText, setSeverity } = useAlert();
  const question = useQuestion();
  const { setCurrentEvent: setRequestDetails, resetRequest } = useRequest();
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  const [partnerOrgDialogOpen, setPartnerOrgDialogOpen] = useState(false);
  const [partnerOrgForDialog, setPartnerOrgForDialog] =
    useState<PartnerOrganization | null>(null);
  const [handlingNoticeDismissed, setHandlingNoticeDismissed] = useState(false);

  const navigateBackToOpportunitiesTable = () => {
    if (embeddedEventId) return;
    void invalidateOpportunitiesTableQueries(queryClient);
    navigate(-1);
    resetRequest();
  };

  useEffect(() => {
    return () => {
      void invalidateOpportunitiesTableQueries(queryClient);
    };
  }, [queryClient]);

  useEffect(() => {
    if (selectedListOverride !== undefined) {
      setSelectedList(selectedListOverride);
      return;
    }
    const params = new URLSearchParams(location.search);
    const fromSearch = params.get("selectedList");
    const standalone = params.get("view") === "relevant-volunteers-only";
    setSelectedList(
      location.state?.selectedList ??
        fromSearch ??
        (standalone ? "relevant-volunteers" : ""),
    );
  }, [
    eventId,
    location.state?.selectedList,
    location.search,
    selectedListOverride,
  ]);

  const {
    data: eventDetails,
    refetch: refetchEvent,
    isLoading,
  } = useQuery({
    queryKey: ["event-card", eventId],
    queryFn: () =>
      opportunityRepo.findId(eventId!, {
        include: {
          manager: true,
          handlingManager: true,
          progressStatus: true,
          categories: { include: { category: true } },
          populations: { include: { population: true } },
          associationRequested: {
            include: {
              user: true,
            },
          },
          teams: {
            include: {
              team: {
                include: {
                  subTeams: false,
                },
              },
            },
          },
          partnerOrganization: true,
          parentOpportunity: true,
        },
      }),
    enabled: !!eventId,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (!embeddedEventId || !eventDetails?.id || eventDetails.handlingManagerId) {
      return;
    }
    const opportunityId = eventDetails.id;
    let cancelled = false;
    void linko.opportunity.mutations
      .claimForHandling({ opportunityId })
      .then((res) => {
        if (cancelled || !res.data.success) return;
        void queryClient.invalidateQueries({ queryKey: ["event-card", opportunityId] });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [
    embeddedEventId,
    eventDetails?.id,
    eventDetails?.handlingManagerId,
    queryClient,
  ]);

  const { data: groupChildCount = 0 } = useQuery({
    queryKey: ["group-parent-child-count", eventId],
    queryFn: () =>
      opportunityRepo.count({
        parentOpportunityId: eventId!,
        isDeleted: false,
      }),
    enabled: !!eventId,
  });
  const isGroupWithInstances =
    !!eventDetails?.isOpportunityGroupParent || groupChildCount > 0;

  useEffect(() => {
    if (selectedListOverride !== undefined) return;
    if (!isGroupWithInstances) return;
    if (selectedList !== "relevant-volunteers" && !isRelevantVolunteersStandalone) {
      return;
    }
    setSelectedList("group-slots");
  }, [
    isGroupWithInstances,
    isRelevantVolunteersStandalone,
    selectedList,
    selectedListOverride,
  ]);

  const showHandlingEntryNotice =
    !!eventDetails?.handlingManagerId &&
    eventDetails.handlingManagerId !== currentUser?.id &&
    !handlingNoticeDismissed;

  useEffect(() => {
    setHandlingNoticeDismissed(false);
  }, [eventDetails?.id]);

  const openPartnerOrganizationDetails = useCallback(async () => {
    const partnerOrgId = eventDetails?.partnerOrganizationId;
    if (!partnerOrgId) return;
    const org = await loadPartnerOrganizationForDetails(
      partnerOrgId,
      eventDetails?.partnerOrganization,
    );
    setPartnerOrgForDialog(org);
    setPartnerOrgDialogOpen(true);
  }, [eventDetails?.partnerOrganization, eventDetails?.partnerOrganizationId]);

  const handleHandlingChanged = useCallback(async () => {
    const result = await refetchEvent();
    const freshUpdatedAt = result.data?.updatedAt;
    if (
      freshUpdatedAt &&
      (selectedList === "edit" || selectedList === "duplicate")
    ) {
      setRequestDetails((prev) => ({ ...prev, updatedAt: freshUpdatedAt }));
    }
  }, [refetchEvent, selectedList, setRequestDetails]);

  useEffect(() => {
    if (selectedList === "duplicate") {
      setRequestDetails((prev) => ({ ...prev, action: "duplicate" }));
    }
    if (selectedList === "delete") {
      deleteEventDialog();
    } else if (selectedList === "restore") {
      restoreEventDialog();
    } else if (selectedList === "edit") {
      if (eventDetails?.isRepeated) editEventDialog();
      else setRequestDetails((prev) => ({ ...prev, action: "edit" }));
    }
  }, [selectedList]);

  const editEventDialog = async () => {
    const res = await question(
      "משימה זו היא משימה חוזרת, האם ברצונך לערוך גם את כל המשימות שקשורות אליה?",
      "עריכת המשימה הזו בלבד תגרום להסרת הקישור שלה לשאר המשימות החוזרות.",
      { confirmText: "לערוך את כולן", cancelText: "רק את המשימה הזו" },
    );
    if (res)
      setRequestDetails((prev) => ({ ...prev, editRepeatedType: "all" }));
    else if (res === false)
      setRequestDetails((prev) => ({ ...prev, editRepeatedType: "one" }));
    else setSelectedList("");
  };

  const deleteEventDialog = async () => {
    const res = await question(
      eventDetails?.isRepeated
        ? "משימה זו היא משימה חוזרת, האם ברצונך למחוק גם את כל המשימות שקשורות אליה?"
        : eventDetails?.isOpportunityGroupParent
          ? ADMIN_GROUP_DELETE_ARCHIVE
          : "האם ברצונך למחוק את המשימה הזו?",
      eventDetails?.isOpportunityGroupParent
        ? ADMIN_GROUP_DELETE_HINT
        : "",
      {
        confirmText: eventDetails?.isRepeated ? "למחוק הכל" : "אישור",
        cancelText: eventDetails?.isRepeated ? "רק את משימה זו" : "ביטול",
      },
    );
    if (res) {
      if (eventDetails?.isRepeated) {
        deleteRepeatedEvent();
      } else {
        deleteEvent();
      }
    } else {
      if (eventDetails?.isRepeated && res === false) {
        deleteEvent();
      }
      setSelectedList("");
    }
  };

  async function deleteRepeatedEvent() {
    if (!eventDetails?.id) return;
    setAlertText("המשימות מועברות לארכיון...");
    setSeverity("warning");
    try {
      const res = await Opportunity.deleteRepeatedRequest(eventDetails);
      setSeverity(res.success ? "success" : "error");
      if (res.success) {
        setAlertText(res.message);
        navigate(-1);
      } else {
        setAlertText("קרתה תקלה בעת העברת המשימות לארכיון");
      }
    } catch (error: any) {
      setSeverity("error");
      setAlertText(error?.message || "קרתה תקלה בעת העברת המשימות לארכיון");
    }
  }

  const restoreEventDialog = async () => {
    const res = await question(
      eventDetails?.isOpportunityGroupParent
        ? ADMIN_GROUP_RESTORE_ARCHIVE
        : "האם ברצונך לשחזר את המשימה מהארכיון?",
      "",
      { confirmText: "שחזור", cancelText: "ביטול" },
    );
    if (!res) {
      setSelectedList("");
      return;
    }
    if (!eventDetails?.id) return;
    try {
      const restoreRes = await Opportunity.restoreRequest(eventDetails.id);
      setSeverity(restoreRes.success ? "success" : "error");
      setAlertText(restoreRes.message);
      if (restoreRes.success) {
        refetchEvent?.();
        setSelectedList("");
      }
    } catch (error: any) {
      setSeverity("error");
      setAlertText(error?.message || "קרתה תקלה בעת שחזור המשימה");
      setSelectedList("");
    }
  };

  const deleteEvent = async () => {
    if (!eventDetails?.id) return;
    try {
      const res = await Opportunity.deleteRequest(eventDetails.id);
      setSeverity(res.success ? "success" : "error");
      if (res.success) {
        setAlertText(res.message);
        navigate(-1);
      } else {
        setAlertText("קרתה תקלה בעת העברת המשימה לארכיון");
      }
    } catch (error: any) {
      setSeverity("error");
      setAlertText(error?.message || "קרתה תקלה בעת העברת המשימה לארכיון");
    }
  };

  return (
    <>
      <PartnerOrganizationDetailsDialog
        open={partnerOrgDialogOpen}
        organization={partnerOrgForDialog}
        onClose={() => {
          setPartnerOrgDialogOpen(false);
          setPartnerOrgForDialog(null);
        }}
      />
      {isLoading ? (
        <Stack
          className="event-card-container"
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      ) : (
        <div
          className={`event-card-container${
            embeddedEventId ? " event-card-embedded" : ""
          }`}
        >
          <div className="event-card-header">
            <div className="titles-container">
              {!embeddedEventId ? (
              <IconButton onClick={navigateBackToOpportunitiesTable}>
                <KeyboardBackspaceRoundedIcon
                  sx={{
                    color: COLORS["main-color"],
                    transform: "rotate(180deg)",
                  }}
                />
              </IconButton>
              ) : null}
              <p
                className="main"
                onClick={embeddedEventId ? undefined : navigateBackToOpportunitiesTable}
              >
                משימות
              </p>
              {eventDetails?.parentOpportunityId && (
                <>
                  <span className="divider">»</span>
                  <p
                    className="main"
                    title={
                      eventDetails.parentOpportunity?.title ??
                      ADMIN_GROUP_INSTANCE_BREADCRUMB_PARENT
                    }
                    onClick={() =>
                      navigate(`/events/${eventDetails.parentOpportunityId}`, {
                        state: { selectedList: "group-slots" },
                      })
                    }
                  >
                    {eventDetails.parentOpportunity?.title ??
                      ADMIN_GROUP_INSTANCE_BREADCRUMB_PARENT}
                  </p>
                </>
              )}
              <span className="divider">»</span>
              <p data-tour={tourAnchors ? "cc-event-title" : undefined}>
                {eventDetails?.title}
              </p>
            </div>
            {eventDetails?.id && !currentUser?.isCallCenterStation && (
              <Stack
                className="event-card-handling-bar"
                direction="row"
                justifyContent="flex-end"
                flexWrap="wrap"
                gap={1}
              >
                <OpportunityHandlingControls
                  variant="header"
                  opportunityId={eventDetails.id}
                  handlingManagerId={eventDetails.handlingManagerId}
                  handlingManagerName={eventDetails.handlingManager?.name}
                  handlingSince={eventDetails.handlingSince}
                  onChanged={() => void handleHandlingChanged()}
                />
              </Stack>
            )}
          </div>
          {eventDetails?.callCenterInVolunteerPool &&
            !currentUser?.isCallCenterStation && (
              <CallCenterVolunteerAssignmentMark layout="banner" />
            )}
          <div
            className={`content-container${
              isRelevantVolunteersStandalone
                ? " event-card-standalone-relevant-volunteers"
                : ""
            }`}
          >
            {!isRelevantVolunteersStandalone && (
              <EventSideBar
                eventDetails={eventDetails || undefined}
                selectedList={selectedList}
                setSelectedList={setSelectedList}
                refetchEvent={refetchEvent}
                tourAnchors={tourAnchors}
                onOpenPartnerOrganization={
                  eventDetails?.partnerOrganizationId
                    ? openPartnerOrganizationDetails
                    : undefined
                }
              />
            )}
            <div
              className={`details-participants-container${
                selectedList === "edit" || selectedList === "duplicate"
                  ? " event-card-edit-layout"
                  : selectedList === "relevant-volunteers"
                    ? isRelevantVolunteersStandalone
                      ? " event-card-relevant-volunteers-standalone-layout"
                      : " event-card-relevant-volunteers-layout"
                    : ""
              }`}
            >
              {showHandlingEntryNotice &&
                !currentUser?.isCallCenterStation &&
                selectedList !== "relevant-volunteers" &&
                !isRelevantVolunteersStandalone && (
                <OpportunityHandlingEntryNotice
                  managerName={
                    eventDetails?.handlingManager?.name ?? "מנהל אחר"
                  }
                  onDismiss={() => setHandlingNoticeDismissed(true)}
                />
              )}
              {(selectedList === "" ||
                (selectedList === "relevant-volunteers" &&
                  !isRelevantVolunteersStandalone)) && (
                <EventDetails
                  displayAll
                  spacious={!!embeddedEventId}
                  eventDetails={eventDetails || undefined}
                  onOpenPartnerOrganization={
                    eventDetails?.partnerOrganizationId
                      ? openPartnerOrganizationDetails
                      : undefined
                  }
                />
              )}
              {selectedList === "reports" && (
                <OpportunityReportTable id={eventDetails?.id || ""} />
              )}
              {selectedList === "send-message" && (
                <SendInDistribution
                  users={
                    eventDetails?.participantUsers
                      ?.map((user) => user.association?.user?.phoneNumber ?? "")
                      .filter((phoneNumber) => phoneNumber !== "") ?? []
                  }
                />
              )}
              {selectedList === "add-mission" && (
                <MissionList
                  filter={eventId ? { opportunityId: eventId } : undefined}
                  mission={{
                    opportunityId: eventId || "",
                    opportunity: eventDetails || undefined,
                  }}
                  missionType="OPPORTUNITY"
                />
              )}
              {selectedList === "registration-analytics" && (
                <EventRegistrationAnalysis
                  eventDetails={eventDetails || undefined}
                />
              )}
              {selectedList === "communication" && eventDetails?.id && (
                <CommunicationRecordManager
                  opportunityId={eventDetails.id}
                  type="opportunity"
                />
              )}
              {selectedList === "relevant-volunteers" &&
                eventDetails?.id &&
                (!isGroupWithInstances || tourAnchors) && (
                <RelevantVolunteersSection
                  opportunityId={eventDetails.id}
                  standalone={isRelevantVolunteersStandalone}
                  tourAnchors={tourAnchors}
                  onSendSuccess={(result) => {
                    setSeverity(
                      result.failed === 0 && !result.skipped ? "success" : "warning",
                    );
                    setAlertText(result.message);
                    refetchEvent?.();
                  }}
                  onAddSuccess={() => {
                    void refetchEvent?.();
                  }}
                />
              )}
              {selectedList === "group-slots" &&
                isGroupWithInstances &&
                eventDetails?.id && (
                  <GroupOpportunitySlots
                    parentId={eventDetails.id}
                    parentTitle={eventDetails.title}
                  />
                )}
              {selectedList !== "edit" &&
                selectedList !== "duplicate" &&
                selectedList !== "reports" &&
                selectedList !== "requests" &&
                selectedList !== "registration-analytics" &&
                selectedList !== "relevant-volunteers" &&
                selectedList !== "group-slots" &&
                selectedList !== "communication" &&
                (!isGroupWithInstances ||
                  (tourAnchors && selectedList === "add-participants")) && (
                  <EventParticipants
                    isAdd={selectedList === "add-participants"}
                    eventDetails={eventDetails || undefined}
                    refetchEvent={refetchEvent}
                    tourAnchors={tourAnchors}
                    tourShowGroups={tourShowGroups}
                  />
                )}
              {(selectedList === "edit" || selectedList === "duplicate") && (
                <OrganizationRequestFullContainer
                  eventData={eventDetails || undefined}
                  selectedList={selectedList}
                  isEventCard
                  isEdit={selectedList === "edit"}
                  onSaveSuccess={(result) => {
                    void refetchEvent?.();
                    if (result?.isDuplicate && result.newId) {
                      navigate(`/events/${result.newId}`);
                      return;
                    }
                    setSelectedList("");
                  }}
                />
              )}
              {selectedList === "requests" && (
                <EventRequests
                  eventDetails={eventDetails || undefined}
                  refetchEvent={() => {
                    void refetchEvent();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
